import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { WidenService } from '@services/widen.service';
import {
  ArticleCollectionContent,
  ArticleHubContent,
  RelatedStrategies,
  InsightsStickyTab,
  StrategiesTab,
} from '@marketing/insights/insights.interface';
import { Logger } from '@utils/logger';
import { LinkService } from '@services/link.service';
import {
  AppStateService,
  LocalLinkService,
  SegmentService,
  TabsStickyService,
  WindowScrollService,
} from '@services';
import { Subject, Subscription } from 'rxjs';
import { ResponsiveService, WindowService } from '@frk/eds-components';
import { takeUntil } from 'rxjs/operators';
import { WINDOW } from '@ng-web-apis/common';
import { ArticleService } from '@marketing/article/services/article.service';
import { InsightsService } from '@marketing/insights/insights.service';
import { StrategyTabsService } from '@services/strategy-tabs.service';
import { StickyTab, StickyTabChange } from '@frk/eds-components';

/**
 * Logger
 */
const logger = Logger.getLogger('ArticleCollectionComponent');

/**
 * The Article Collection component
 *
 * Selector: `ft-article-collection`
 *
 * Exported as: `ArticleCollectionComponent`
 */
@Component({
  selector: 'ft-article-collection',
  templateUrl: './article-collection.component.html',
  styleUrls: ['article-collection.component.scss'],
})
export class ArticleCollectionComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  // Component fields
  content: ArticleCollectionContent;
  translationLabels: any;

  public relatedContentHeaderTypeLabel: string;
  compactdet = false;
  public showButtons = false;
  sidebarOffsetTop: number;
  sidebarHeight: number;
  segmentTags: string;
  audienceExclusions?: Array<string>;
  public showSegment = false;
  // Element Refs
  @ViewChild('sidebar') sidebar: ElementRef;

  // Observables
  isMobile$: Subscription;
  isMobile: boolean;

  /**
   * SIM logo resource bundle object
   */
  simLogoResourceBundle: object;

  // Private fields
  private unsubscribe$: Subject<void> = new Subject<void>();
  public relatedStrategies: RelatedStrategies;
  private articleCollectionInfo: ArticleHubContent;

  public stickyTabs: StickyTab[];

  public activeTabId: string;
  public showArticle = false;
  /**
   * Constructor
   */
  constructor(
    @Inject(WINDOW) private windowRef: Window,
    private appStateService: AppStateService,
    private widenService: WidenService,
    private ctaLinkService: LinkService,
    private responsiveService: ResponsiveService,
    private windowService: WindowService,
    private cdr: ChangeDetectorRef,
    private segmentService: SegmentService,
    private articleService: ArticleService,
    private insightsService: InsightsService,
    private localLinkService: LocalLinkService,
    private strategyTabsService: StrategyTabsService,
    private tabsStickyService: TabsStickyService,
    private scrollService: WindowScrollService
  ) {
    super();
    this.segmentTags = this.segmentService.getCurrentSegment()?.insightAudiences;
  }

  /**
   * Angular Init Hook
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.setContentData();

    // Related Strategies
    this.articleCollectionInfo = this.document?.getData<ArticleHubContent>();

    // Sticky Nav
    if (this.articleCollectionInfo?.stickyNav) {
      this.stickyTabs = this.strategyTabsService.getStickyNavigation(
        this.articleCollectionInfo.stickyNav,
        this.segmentTags
      );
    }

    // Strategy Tabs
    if (this.articleCollectionInfo?.strategySectionCompound) {
      this.relatedStrategies = this.strategyTabsService.createStrategyTabsSection(
        this.articleCollectionInfo?.strategySectionCompound,
        this.segmentTags,
        this.page
      );
    }

    // Set section nav in service
    this.tabsStickyService.setStickyTabs(this.stickyTabs);

    // Listen for active tab change and update highlight

    this.tabsStickyService
      .getActiveTabId$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((id: string): void => {
        logger.debug('getActiveTabId$', id);
        this.activeTabId = id;
        this.cdr.detectChanges();
      });
  }

  private setContentData(): void {
    this.content = this.document?.getData<ArticleCollectionContent>();

    // show stickty side bar if localizable data has isAllowed property as true;
    if (
      this.content &&
      this.content.relatedContent.length &&
      this.content.relatedContent[0].localizableLink.length
    ) {
      this.content.relatedContent[0].localizableLink.forEach((data) => {
        if (data.isAllowed) {
          this.showArticle = true;
        }
      });
    }

    // Responsive behaviours
    this.isMobile$ = this.responsiveService
      .isMobile$()
      ?.subscribe((isMobile) => (this.isMobile = isMobile));

    if (this.isMobile) {
      this.showButtons = true;
      this.compactdet = true;
    }

    this.windowService
      .getScrollObs$(100)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.isMobile) {
          return;
        }
        const windowScroll = this.windowRef.scrollY;
        this.sidebarOffsetTop = this.sidebar.nativeElement.offsetTop;
        this.sidebarHeight = this.sidebar.nativeElement.offsetHeight;
        this.showButtons =
          windowScroll > this.sidebarOffsetTop + this.sidebarHeight;
        this.cdr.detectChanges();
      });

    // Load translations from resource bundles
    this.translationLabels = this.component?.getModels()[
      'ft.core.insightsLibrary'
    ];
    this.relatedContentHeaderTypeLabel = this.component?.getModels()[
      'ft.core.article-related-content'
    ];
    this.simLogoResourceBundle = this.component?.getModels()[
      'ft.core.sim-logos'
    ];
    // Article Related Content
    if (this.content?.relatedContent?.length) {
      this.articleService.getRelatedContent(
        this.segmentTags,
        this.content,
        this.page
      );
    }

    if (this.content?.audienceExclusions) {
      this.showSegment = this.articleService.isSegmentTagAvailable(
        this.segmentTags,
        JSON.stringify(this.content?.audienceExclusions)
      );
    } else {
      this.showSegment = false;
    }

    // SIM logo for collection sidebar
    this.content.SIM = this.insightsService.getSIMLogo(
      this.content.logo as string,
      false,
      this.simLogoResourceBundle
    );

    // Set banner breadcrumbs
    this.setBreadcrumbItems();

    // Format banner image
    if (this.content?.bannerImage) {
      this.content.bannerImage = this.widenService.getWidenAssetUrl(
        this.content.bannerImage.widenDocument
      );
    }

    // Format cards in main body & right rail
    this.formatBlocks(this.content?.content);
    this.formatBlocks(this.content?.rightRail, true);
    this.formatBlocks(this.content?.lowerContent);
  }
  /**
   * Fomrat the card compound for display
   * @param card cardCompound object
   */
  formatBlocks(blocks: any, isRightRail = false): void {
    for (const block of blocks) {
      // Check should we show it
      if (block.targeting) {
        block.isAllowed = this.articleService.isSegmentTagAvailable(
          this.segmentTags,
          block.targeting
        );
      } else {
        block.isAllowed = true;
      }
      // Card compound array
      if (block.cardCompound) {
        for (const card of block.cardCompound) {
          // Format image
          if (card.image) {
            card.image = this.widenService.getWidenAssetUrl(
              card.image.widenAsset
            );
          }
          // Format link
          if (card.link?.linkCollection?.length) {
            card.link = card.link.linkCollection[0];
            const documentRef = card.link?.document?.$ref;
            const internalLink = this.articleService.getCardLink(
              this.page,
              documentRef
            );

            card.href = internalLink
              ? internalLink
              : this.ctaLinkService.getCTALink(
                  this.page,
                  card.link,
                  this.appStateService?.getHomePageUrl()
                );
            card.isNewWindow = card.link.target === '_blank';
            card.externalLink = card.link?.external;
          }
        }
      }

      if (block.type === 'ParagraphBlock') {
        block.content = this.localLinkService.getLocalizeLinks(block.content);
      }
      // End Card formatting

      block.isRightRail = isRightRail;

      // Bottom spacing for allowed blocks
      block.bottomSpacing = block.isAllowed
        ? block.spacingBottom
          ? block.spacingBottom
          : 'xl'
        : 'none';
    }
  }

  // called when sticky tab clicked
  public onTabChange(tabEvent: StickyTabChange): void {
    logger.debug('onTabChange()', tabEvent);
    if (tabEvent.type !== 'manual') {
      // this will suppress ScrollSpy for non-scroll events
      this.tabsStickyService.tabClicked(tabEvent.tab);
    }
  }

  /**
   * sticky tabs height has changed
   * @param id - tab id
   */
  public onHeightChange(height: number): void {
    logger.debug('onHeightChange()', height);
    // this will update the scroll offset position based on tabs height
    this.scrollService.stickyTabsResize(
      'article-collection-sticky-tabs',
      height
    );
  }

  /**
   * Angular hook
   */
  ngOnDestroy() {
    this.isMobile$?.unsubscribe();
  }
}
